import React from 'react';
import { Flash, GraduationCap, Github, ArrowUpRight, Code } from 'iconoir-react';
import AnimatedElement, { StaggerContainer, StaggerItem } from '../ui/AnimatedElement';

const Projects: React.FC = () => {
  const projects = [
    {
      icon: Flash,
      title: 'Procurement Auto',
      description: 'AI-powered automation suite for procurement workflows. Saved 20+ hours weekly through automated data entry and report generation.',
      tech: ['Python', 'Gemini API', 'VBA'],
      accent: 'matcha',
    },
    {
      icon: GraduationCap,
      title: 'Student Data Manager',
      description: 'Academic project with binary search tree implementation for efficient student record retrieval and management.',
      tech: ['C++', 'Data Structures'],
      accent: 'slate',
    },
  ];

  return (
    <div className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      {/* Atmospheric Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-50 via-white to-slate-100" />

      {/* Subtle decorative elements */}
      <div
        className="absolute top-0 right-0 w-1/2 h-1/2 opacity-30"
        style={{
          background: 'radial-gradient(ellipse at top right, rgba(74, 103, 65, 0.08) 0%, transparent 60%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-1/2 h-1/2 opacity-20"
        style={{
          background: 'radial-gradient(ellipse at bottom left, rgba(74, 103, 65, 0.06) 0%, transparent 60%)',
        }}
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(0,0,0,0.3) 1px, transparent 1px)`,
          backgroundSize: '80px 80px',
        }}
      />

      <div className="relative z-10 w-full max-w-4xl mx-auto flex flex-col justify-center p-4 md:p-8">
        <AnimatedElement variant="fadeUp" className="mb-10">
          <span className="text-neutral-500 text-sm font-mono tracking-wide">05 — PROJECTS</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mt-3 tracking-tight">
            Other Work
          </h2>
          <p className="text-neutral-500 mt-3 max-w-lg">
            Side projects and explorations in automation and data structures.
          </p>
        </AnimatedElement>

        <StaggerContainer className="space-y-5">
          {projects.map((project, index) => (
            <StaggerItem key={index}>
              <div className="group relative">
                {/* Accent border on left */}
                <div
                  className={`absolute left-0 top-4 bottom-4 w-1 rounded-full transition-all duration-300 ${
                    project.accent === 'matcha'
                      ? 'bg-matcha-400/40 group-hover:bg-matcha-500'
                      : 'bg-slate-300/50 group-hover:bg-slate-400'
                  }`}
                />

                <div className="relative ml-4 p-6 bg-white/80 backdrop-blur-sm rounded-2xl border border-slate-200/80 shadow-sm transition-all duration-300 group-hover:shadow-xl group-hover:border-slate-300 group-hover:bg-white">
                  {/* Header with icon and title */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-3 rounded-xl transition-all duration-300 ${
                          project.accent === 'matcha'
                            ? 'bg-matcha-50 group-hover:bg-matcha-100'
                            : 'bg-slate-100 group-hover:bg-slate-200'
                        }`}
                      >
                        <project.icon
                          className={`transition-colors duration-300 ${
                            project.accent === 'matcha'
                              ? 'text-matcha-600'
                              : 'text-slate-500'
                          }`}
                          width={24}
                          height={24}
                          strokeWidth={1.5}
                        />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{project.title}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Code width={12} height={12} className="text-slate-400" />
                          <span className="text-xs text-slate-400 font-mono">
                            {project.tech.join(' · ')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-slate-600 text-sm leading-relaxed mb-5">
                    {project.description}
                  </p>

                  {/* Tech tags */}
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors duration-300 ${
                          project.accent === 'matcha'
                            ? 'bg-matcha-50/80 text-matcha-700 group-hover:bg-matcha-100'
                            : 'bg-slate-100 text-slate-600 group-hover:bg-slate-200'
                        }`}
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </StaggerItem>
          ))}

          {/* GitHub Link Card - Distinctive design */}
          <StaggerItem>
            <a
              href="https://github.com/AnthonyA671"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block mt-6"
            >
              {/* Decorative background element */}
              <div className="absolute -inset-1 bg-gradient-to-r from-matcha-500/20 via-transparent to-matcha-500/20 rounded-2xl opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500" />

              <div className="relative flex items-center justify-between p-6 bg-slate-900 rounded-2xl border border-slate-800 transition-all duration-300 group-hover:border-matcha-500/30 overflow-hidden">
                {/* Subtle pattern overlay */}
                <div
                  className="absolute inset-0 opacity-5"
                  style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 1px)`,
                    backgroundSize: '24px 24px',
                  }}
                />

                <div className="relative flex items-center gap-4">
                  <div className="p-3 bg-white/10 rounded-xl border border-white/10 transition-all duration-300 group-hover:bg-matcha-500/20 group-hover:border-matcha-500/30">
                    <Github className="text-white" width={24} height={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-lg">View More on GitHub</h3>
                    <p className="text-slate-400 text-sm mt-0.5">Explore my repositories and contributions</p>
                  </div>
                </div>

                <div className="relative p-3 rounded-xl bg-white/5 border border-white/10 transition-all duration-300 group-hover:bg-matcha-500 group-hover:border-matcha-500 group-hover:translate-x-1 group-hover:-translate-y-1">
                  <ArrowUpRight className="text-slate-400 group-hover:text-white transition-colors" width={20} height={20} />
                </div>
              </div>
            </a>
          </StaggerItem>
        </StaggerContainer>
      </div>
    </div>
  );
};

export default Projects;
